import { Box, TextField } from "@material-ui/core";
import firebase from "firebase/compat";
import { geohashForLocation } from "geofire-common";
import { FC, useEffect, useRef, useState } from "react";
import {
  Edit,
  EditProps,
  email,
  FormGroupContextProvider,
  FormWithRedirect,
  minLength,
  PasswordInput,
  required,
  SelectInput,
  TextInput,
  useLoading,
} from "react-admin";
import { Field } from "react-final-form";

import CustomCard from "../../components/CustomCard";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import Location from "../../components/Location";
import Spacing from "../../components/Spacing";
import { QATAR_CODE_PREFIX } from "../../constants/general";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import firebaseConfig from "../../init/config";
import { firestore } from "../../init/firebase";
import { GenericObject } from "../../types/generic.types";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { AppResource } from "../../types/resource.types";
import { areEqual } from "../../utils/generic";
import { isExists, isPhoneNumber, validateFileSize } from "../../utils/validators";
import SocialMediaLinks from "../Gym/components/SocialMediaLinks";
import PhonePrefixCode from "./components/PhonePrefixCode";
import useEntertainmentsLocalizations from "./utils/useEntertainmentsLocalizations";

const CustomForm: FC<CustomEditProps> = (props) => {
  const entertainment = props.record;
  entertainment.phone = entertainment.phone.replace(QATAR_CODE_PREFIX, "");
  entertainment.whatsapp = entertainment.whatsapp.replace(QATAR_CODE_PREFIX, "");
  const { socialsRef, locationRef, emailRef } = props;

  const loading = useLoading();
  const localizations = useEntertainmentsLocalizations(props, false);
  const [enteredLocationUrl, setEnteredLocationUrl] = useState<GenericObject | null>(null);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const { disabledStyles } = useDisabledOnLoading(loading || locationLoading);

  const entertainmentsQuery = (key: string, value: string) =>
    firestore.collection(AppResource.entertainments).where(key, "==", value).limit(1);

  const [socialLinksError, setSocialLinksError] = useState(false);
  const [socials, setSocials] = useState(entertainment.socials);

  const [emailError, setEmailError] = useState<boolean | string>("");
  const [enteredEmail, setEnteredEmail] = useState(entertainment.email);

  const emailHandler = async (e: any) => {
    const value = e.target.value;
    setEnteredEmail(value);
    if (value.trim() && value === entertainment.email) {
      setEmailError("");
      emailRef.current = entertainment.email;
    } else if (value.trim()) {
      const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      if (regex.test(value)) {
        const urlencoded = new URLSearchParams();
        urlencoded.append("email", value);
        const response = await fetch(`${firebaseConfig.cloudFunctionsBaseUrl}/functions-checkingAuth`, {
          method: "post",
          body: urlencoded,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });
        if (response.status === 200) {
          emailRef.current = value;
          setEmailError("");
        } else {
          setEmailError("Email already exists");
        }
      } else {
        setEmailError("Invalid email");
      }
    } else {
      setEmailError(true);
    }
  };

  const handleLocationUrl = async (e: any) => {
    if (e.target.value.trim()) {
      try {
        setLocationLoading(true);
        const response = await fetch(
          `${firebaseConfig.cloudFunctionsBaseUrl}/functions-getLatLng?url=${e.target.value}`
        );
        const data = await response.json();
        setEnteredLocationUrl(data);
        setLocationLoading(false);
      } catch (ex: any) {
        console.log(ex.message);
        setLocationLoading(false);
      }
    }
  };

  useEffect(() => {
    locationRef.current = entertainment.location;
    emailRef.current = entertainment.email;
  }, []);
  useEffect(() => {
    socialsRef.current = socials;
  }, [socials]);

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.entertainmentsEdit}>
        <FormWithRedirect
          {...props}
          redirect={`/${AppResource.entertainments}`}
          render={(formProps) => {
            return (
              <form onSubmit={formProps.handleSubmit} style={disabledStyles}>
                <CustomCard title="Details">
                  <Spacing>
                    {localizations?.[0]}
                    {localizations?.[1]}
                  </Spacing>

                  <Spacing>
                    {/* <TextInput
                      source="email"
                      variant="outlined"
                      fullWidth
                      validate={[
                        required(),
                        email(),
                        isExists({ query: entertainmentsQuery, type: "email", currentValue: entertainment.email }),
                      ]}
                    /> */}
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      onChange={emailHandler}
                      style={{ marginTop: 8 }}
                      label="Email"
                      helperText={enteredEmail.length && emailError ? emailError : ""}
                      error={!!enteredEmail && !!emailError}
                      defaultValue={enteredEmail}
                    />

                    <PasswordInput
                      source="password"
                      variant="outlined"
                      type="password"
                      fullWidth
                      validate={minLength(8)}
                    />
                  </Spacing>

                  <Spacing>
                    <TextInput
                      source="phone"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: <PhonePrefixCode />,
                      }}
                      validate={[
                        required(),
                        isExists({
                          query: entertainmentsQuery,
                          type: "phone",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: entertainment.phone,
                        }),
                        isExists({
                          query: entertainmentsQuery,
                          type: "whatsapp",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: entertainment.phone,
                        }),
                        isPhoneNumber(),
                      ]}
                    />

                    <TextInput
                      source="whatsapp"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: <PhonePrefixCode />,
                      }}
                      validate={[
                        required(),
                        isExists({
                          query: entertainmentsQuery,
                          type: "whatsapp",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: entertainment.whatsapp,
                        }),
                        isExists({
                          query: entertainmentsQuery,
                          type: "phone",
                          prefix: QATAR_CODE_PREFIX,
                          currentValue: entertainment.whatsapp,
                        }),
                        isPhoneNumber("whatsapp"),
                      ]}
                    />
                  </Spacing>

                  <Spacing>
                    {localizations?.[2]}
                    {localizations?.[3]}
                  </Spacing>
                  <SelectInput
                    source="status"
                    choices={[
                      { id: "disabled", name: "Disabled" },
                      { id: "enabled", name: "Enabled" },
                    ]}
                    variant="outlined"
                    validate={[required()]}
                    fullWidth
                    label="Status"
                  />
                </CustomCard>

                <CustomCard title="Social media links">
                  <SocialMediaLinks
                    handleSocialLinks={(socialLinks: any) => setSocials(socialLinks)}
                    handleSocialLinksError={(value: boolean) => setSocialLinksError(value)}
                    defaultValue={entertainment.socials}
                  />
                </CustomCard>

                <CustomCard title="Logo">
                  <ImageUploadInput source="logo" validate={[required(), validateFileSize("logo")]} />
                </CustomCard>

                <CustomCard title="Address">
                  <Spacing>
                    {localizations?.[4]}
                    {localizations?.[5]}
                  </Spacing>
                </CustomCard>

                <CustomCard title="Location">
                  <Box mr={2} mb={1}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      onChange={handleLocationUrl}
                      label="Location url"
                    />
                  </Box>
                  <Field name="location" validate={required()}>
                    {(fieldProps) => (
                      <Location
                        onLocationChange={(data) => {
                          if (!areEqual(data, location)) {
                            setLocation(data);
                            setEnteredLocationUrl(data);
                            const geohash = geohashForLocation([data.lat, data.lng]);
                            const geopoint = new firebase.firestore.GeoPoint(data.lat, data.lng);
                            locationRef.current = { geopoint, geohash };
                            fieldProps.input.onChange({ geopoint, geohash });
                          }
                        }}
                        value={
                          enteredLocationUrl && (!isNaN(+enteredLocationUrl.lat) || !isNaN(+enteredLocationUrl.lng))
                            ? { latitude: +enteredLocationUrl.lat, longitude: +enteredLocationUrl.lng }
                            : null
                        }
                        defaultValue={entertainment.location.geopoint}
                      />
                    )}
                  </Field>
                </CustomCard>

                <CustomToolbar
                  {...formProps}
                  formName={FormGroupNames.entertainmentsEdit}
                  customErrors={{
                    socials: socialLinksError ? "Error on Social media links" : "",
                    location: location ? "" : "Location is required",
                    email: (enteredEmail.length === 0 && emailError) || (!!enteredEmail && emailError),
                  }}
                />
              </form>
            );
          }}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const EntertainmentsEdit: FC<EditProps> = (props) => {
  const socialsRef = useRef<any[]>([]);
  const locationRef = useRef();
  const emailRef = useRef();

  return (
    <Edit
      {...props}
      component="section"
      transform={(values) => {
        values.phone = `+974${values.phone}`;
        values.whatsapp = `+974${values.whatsapp}`;
        values.socials = socialsRef.current;
        values.location = locationRef.current;
        values.email = emailRef.current;
        return values;
      }}
    >
      <CustomForm sanitizeEmptyValues={false} socialsRef={socialsRef} locationRef={locationRef} emailRef={emailRef} />
    </Edit>
  );
};

interface CustomEditProps {
  [key: string]: any;
}

export default EntertainmentsEdit;
